import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath: string;
  jsonLd?: object | object[];
}

/** Injects <title>, <meta>, <link rel="canonical"> and JSON-LD <script> tags
 *  into document.head. Cleans up JSON-LD on unmount / route change.
 *  Never touches index.html directly.
 */
export default function SEO({ title, description, keywords, canonicalPath, jsonLd }: SEOProps) {
  useEffect(() => {
    const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? '';
    const canonicalUrl = `${siteUrl}${canonicalPath}`;

    // --- title ---
    document.title = title;

    // --- meta helper ---
    const setMeta = (attr: string, attrVal: string, content: string) => {
      let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${attrVal}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('name', 'description', description);
    if (keywords) setMeta('name', 'keywords', keywords);

    // OG tags
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('property', 'og:type', 'website');

    // Twitter card
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);

    // --- canonical ---
    let canonEl = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonEl) {
      canonEl = document.createElement('link');
      canonEl.rel = 'canonical';
      document.head.appendChild(canonEl);
    }
    canonEl.href = canonicalUrl;

    // --- last-modified ---
    setMeta('name', 'last-modified', new Date().toISOString().slice(0, 10));

    // --- JSON-LD: remove previous page schemas, inject new ones ---
    document.querySelectorAll('script[data-page-jsonld]').forEach((el) => el.remove());

    if (jsonLd) {
      const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      schemas.forEach((schema) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-page-jsonld', 'true');
        script.textContent = JSON.stringify(schema, null, 2);
        document.head.appendChild(script);
      });
    }

    return () => {
      document.querySelectorAll('script[data-page-jsonld]').forEach((el) => el.remove());
    };
  }, [title, description, keywords, canonicalPath, jsonLd]);

  return null;
}
